/** Agent feed — single scrollable stream of messages + chat header + composer. */
import { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { Loader2, ChevronDown } from "lucide-react";
import { useDevAgentStore } from "@/stores/devAgentStore";
import { useShallow } from "zustand/react/shallow";
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import { FeedItem } from "./components/FeedItem";
import { StreamingText } from "./components/StreamingText";
import { ChatHeaderBar } from "./components/ChatHeaderBar";
import { submitFeedback, type MCPTool } from "./devAgentService";
import { AgentFeedEmptyState } from "./agentFeed/AgentFeedEmptyState";
import { AgentFeedComposer } from "./agentFeed/AgentFeedComposer";
import { useAgentFeedUpload } from "./agentFeed/useAgentFeedUpload";
import { useAgentFeedScroll } from "./agentFeed/useAgentFeedScroll";
import { buildOutgoingMessage } from "./agentFeed/buildOutgoingMessage";
import { computeApproxContextTokens } from "./agentFeed/runDevAgentContextCompress";

interface Props {
  onSendMessage: (msg: string, displayText?: string) => void | Promise<void>;
  onStop?: () => void;
  disabled?: boolean;
  mcpTools?: MCPTool[];
  onNewSession?: () => void;
  currentSessionTitle?: string;
  /** Re-open SSE without full page reload (Chat header Retry). */
  onReconnectSSE?: () => void;
}

export function AgentFeed({
  onSendMessage,
  onStop,
  disabled,
  mcpTools = [],
  onNewSession,
  currentSessionTitle,
  onReconnectSSE,
}: Props) {
  const orgName = useAuthStoreV1((s) => s.selectedOrg?.orgName || "");
  const {
    feedItems, streamingContent, runStatus, suggestedActions,
    connectionStatus, sessionUuid, pipelineSteps,
  } = useDevAgentStore(
    useShallow((s) => ({
      feedItems: s.feedItems,
      streamingContent: s.streamingContent,
      runStatus: s.runStatus,
      suggestedActions: s.suggestedActions,
      connectionStatus: s.connectionStatus,
      sessionUuid: s.sessionUuid,
      pipelineSteps: s.pipelineSteps,
    }))
  );
  const messageCount = feedItems.filter((f) => f.type === "chat_user" || f.type === "chat_agent").length;
  const visibleItems = feedItems.filter((f) =>
    [
      "chat_user",
      "chat_agent",
      "clarification",
      "error",
      "plan",
      "approval",
      "checkpoint",
      "intent_confirm",
    ].includes(f.type),
  );
  const isEmpty = visibleItems.length === 0 && !streamingContent;

  const totalTokens = useMemo(() => computeApproxContextTokens(feedItems), [feedItems]);

  const [input, setInput] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const {
    uploadedFiles,
    setUploadedFiles,
    uploading,
    fileInputRef,
    handleFileUpload,
  } = useAgentFeedUpload();

  const isRunning = runStatus === "running";
  const [historyLoading, setHistoryLoading] = useState(false);
  useEffect(() => {
    if (sessionUuid && visibleItems.length === 0 && !streamingContent) {
      setHistoryLoading(true);
      const t = setTimeout(() => setHistoryLoading(false), 3000);
      return () => clearTimeout(t);
    }
    setHistoryLoading(false);
  }, [sessionUuid, visibleItems.length, streamingContent]);
  const showProcessing = sendingMessage && !isRunning && !streamingContent;

  useEffect(() => {
    if (
      isRunning ||
      streamingContent ||
      runStatus === "completed" ||
      runStatus === "failed" ||
      runStatus === "idle"
    ) {
      setSendingMessage(false);
    }
  }, [isRunning, streamingContent, runStatus]);

  useEffect(() => {
    if (!sendingMessage) return;
    const t = setTimeout(() => setSendingMessage(false), 15000);
    return () => clearTimeout(t);
  }, [sendingMessage]);

  const [stuckSince, setStuckSince] = useState<number | null>(null);
  const [showRetryBanner, setShowRetryBanner] = useState(false);
  const lastFeedCount = useRef(feedItems.length);
  useEffect(() => {
    if (isRunning) {
      if (
        feedItems.length > lastFeedCount.current ||
        streamingContent ||
        pipelineSteps.length > 0
      ) {
        lastFeedCount.current = feedItems.length;
        setStuckSince(null);
        setShowRetryBanner(false);
      } else if (!stuckSince) {
        setStuckSince(Date.now());
      }
    } else {
      setStuckSince(null);
      setShowRetryBanner(false);
      lastFeedCount.current = feedItems.length;
    }
  }, [isRunning, feedItems.length, streamingContent, stuckSince, pipelineSteps.length]);

  useEffect(() => {
    if (!stuckSince) return;
    const t = setTimeout(() => {
      if (useDevAgentStore.getState().runStatus === "running") {
        setShowRetryBanner(true);
      }
    }, 60000);
    return () => clearTimeout(t);
  }, [stuckSince]);

  const streamingLen = streamingContent?.length ?? 0;
  const { bottomRef, scrollContainerRef, showScrollDown } = useAgentFeedScroll({
    feedItemCount: feedItems.length,
    streamingLength: streamingLen,
    isStreaming: !!streamingContent,
  });

  const handleSend = useCallback(
    (msg?: string) => {
      const text = (msg || input).trim();
      if (!text || disabled || isRunning || !!streamingContent) return;
      const pf = useDevAgentStore.getState().pendingFile;
      const { fullText, displayText } = buildOutgoingMessage({
        text,
        uploadedFiles,
        pendingFile: pf,
      });
      setSendingMessage(true);
      void Promise.resolve(onSendMessage(fullText, displayText)).catch(() => {});
      if (!msg) setInput("");
      if (pf) useDevAgentStore.getState().setPendingFile(null);
      setUploadedFiles([]);
    },
    [input, disabled, isRunning, streamingContent, onSendMessage, uploadedFiles],
  );

  const handleFeedback = useCallback((itemId: string, quality: "good" | "bad") => {
    const state = useDevAgentStore.getState();
    const uuid = state.sessionUuid;
    if (!uuid) return;

    const items = state.feedItems;
    const itemIdx = items.findIndex((f) => f.id === itemId);
    let query = "";
    for (let i = itemIdx - 1; i >= 0; i--) {
      if (items[i].type === "chat_user") {
        query = items[i].data.content || "";
        break;
      }
    }

    submitFeedback({
      session_id: 0,
      message_id: itemId,
      score: quality === "good" ? 1.0 : 0.0,
      query,
      org_id: undefined,
    }).catch(() => {});
  }, []);

  return (
    <div className="flex min-h-0 h-full w-full flex-col overflow-hidden">
      <ChatHeaderBar
        connectionStatus={connectionStatus}
        mcpToolCount={mcpTools.length}
        messageCount={messageCount}
        currentSessionTitle={currentSessionTitle}
        orgName={orgName}
        disabled={disabled}
        sessionUuid={sessionUuid}
        approxContextTokens={totalTokens}
        onNewSession={onNewSession}
        onReconnectSSE={onReconnectSSE}
        className="shrink-0"
      />
      <div
        ref={scrollContainerRef}
        data-agent-feed-scroll
        className="flex-1 min-h-0 overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-muted-foreground/20 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-muted-foreground/40"
      >
        <div className="mx-auto p-4 pb-28">
          {historyLoading && visibleItems.length === 0 && (
            <div className="flex items-center justify-center py-20">
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                <p className="text-xs text-muted-foreground">
                  Loading conversation...
                </p>
              </div>
            </div>
          )}

          {isEmpty && !historyLoading && (
            <AgentFeedEmptyState
              mcpTools={mcpTools}
              onQuickSend={(q) => handleSend(q)}
              sendDisabled={isRunning || !!streamingContent}
            />
          )}

          <div className="space-y-1">
            {feedItems.map((item) => (
              <div key={item.id} data-feed-item-id={item.id}>
                <FeedItem
                  item={item}
                  onFeedback={handleFeedback}
                  onSendMessage={onSendMessage}
                  disabled={isRunning || !!streamingContent}
                />
              </div>
            ))}
            {isRunning && !streamingContent && pipelineSteps.length > 0 && (
              <div className="py-3 space-y-1">
                {pipelineSteps.slice(-2).map((step, i) => (
                  <div
                    key={i}
                    className={`flex items-center gap-2 text-xs ${step.status === "running" ? "text-foreground" : "text-muted-foreground"}`}
                  >
                    {step.status === "running" ? (
                      <Loader2 className="w-3 h-3 animate-spin text-blue-400 shrink-0" />
                    ) : (
                      <span className="w-3 h-3 flex items-center justify-center text-green-500 shrink-0">
                        ✓
                      </span>
                    )}
                    <span>{step.label}</span>
                  </div>
                ))}
              </div>
            )}
            {streamingContent && <StreamingText content={streamingContent} />}
            {(showProcessing || (isRunning && !streamingContent)) &&
              pipelineSteps.length === 0 && (
                <div className="flex items-center gap-2 py-3 text-xs text-muted-foreground animate-pulse">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  <span>Processing...</span>
                </div>
              )}
            {showRetryBanner && (
              <div className="flex items-center gap-3 py-2 px-3 text-xs bg-amber-500/10 border border-amber-500/25 dark:border-amber-500/20 rounded-lg">
                <span className="text-amber-800 dark:text-amber-400">
                  No response after 60s.
                </span>
                <button
                  type="button"
                  onClick={() => {
                    onStop?.();
                    setShowRetryBanner(false);
                    setStuckSince(null);
                    const lastUser = [...feedItems]
                      .reverse()
                      .find((f) => f.type === "chat_user");
                    if (lastUser?.data?.content) {
                      setTimeout(() => onSendMessage(lastUser.data.content), 500);
                    }
                  }}
                  className="px-2 py-1 rounded bg-amber-200/90 text-amber-950 hover:bg-amber-300/90 dark:bg-amber-500/20 dark:text-amber-200 dark:hover:bg-amber-500/30 transition-colors"
                >
                  Retry
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onStop?.();
                    setShowRetryBanner(false);
                    setStuckSince(null);
                  }}
                  className="px-2 py-1 rounded bg-muted/50 text-muted-foreground hover:bg-muted/50 transition-colors"
                >
                  Stop
                </button>
              </div>
            )}
            {!isRunning &&
              !streamingContent &&
              suggestedActions.length > 0 &&
              feedItems.length > 0 && (
                <div className="flex flex-wrap gap-2 py-3">
                  {suggestedActions.map((action, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => handleSend(action.query)}
                      className="px-3 py-1.5 text-xs border border-blue-500/40 bg-blue-500/10 rounded-full hover:bg-blue-500/20 hover:border-blue-500/60 text-blue-600 dark:text-blue-300 transition-all hover:shadow-[0_0_8px_rgba(59,130,246,0.15)]"
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              )}
            <div ref={bottomRef} className="h-24" />
          </div>
        </div>
        {showScrollDown && (
          <button
            type="button"
            onClick={() =>
              bottomRef.current?.scrollIntoView({ behavior: "smooth" })
            }
            className="sticky bottom-4 left-1/2 -translate-x-1/2 z-10 rounded-full bg-primary shadow-lg p-2 opacity-80 hover:opacity-100 transition-opacity"
            title="Scroll to bottom"
          >
            <ChevronDown className="w-4 h-4 text-primary-foreground" />
          </button>
        )}
      </div>

      <AgentFeedComposer
        input={input}
        setInput={setInput}
        sendingMessage={sendingMessage}
        uploadedFiles={uploadedFiles}
        setUploadedFiles={setUploadedFiles}
        uploading={uploading}
        fileInputRef={fileInputRef}
        disabled={disabled}
        isRunning={isRunning}
        streamingContent={streamingContent}
        onStop={onStop}
        handleSend={handleSend}
        handleFileUpload={handleFileUpload}
        totalTokens={totalTokens}
      />
    </div>
  );
}
